import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { AllClickTypes } from "@custom-types/types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DateTimeUtils } from "@stellar/utils";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { getSphereViewerUrl, getWebEditorUrl } from "@utils/project-utils";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { UseTrackEvent } from "@utils/track-event/use-track-event";

export type OpenSnapshotProps = {
  /** The ID of the snapshot to open */
  snapshotId: string;

  /** Application target to open the project, either Sphere Viewer or WebEditor */
  openTarget?: ProjectLaunchTarget;
};

/** Gets the URL to open the snapshot for the provided target. */
export function getOpenSnapshotHref({
  openTarget,
  snapshotId,
}: OpenSnapshotProps): string {
  return openTarget === ProjectLaunchTarget.sphereViewer
    ? getSphereViewerUrl({
        projectId: snapshotId,
      }).href
    : getWebEditorUrl(snapshotId, "player").href;
}

type TrackSnapshotProps = Pick<UseTrackEvent, "trackAsyncEvent"> & {
  /** To recognize exactly where the snapshot is open from */
  openFrom:
    | "snapshots-menu-options"
    | "snapshot-sidepanel"
    | "snapshot-table-row";

  /** Number of members in the snapshot to use as props in tracking event. */
  numberOfMembers?: number;

  /** Application target to open the project, either Sphere Viewer or WebEditor */
  openTarget?: ProjectLaunchTarget;

  /** Type of click that triggered the open action */
  clickType?: AllClickTypes;

  /** Timestamp when the snapshot was created */
  createdAt: number;

  /** Access level of the user for the snapshot */
  accessLevel: FaroCoreApiTypes.ProjectAccessLevel;
};

/**
 * Tracks whenever a user wants to open a snapshot in the Sphere Viewer or WebEditor.
 * This function does not open the snapshot, it only tracks the event.
 */
export async function trackOpenSnapshot({
  openFrom,
  numberOfMembers,
  openTarget = ProjectLaunchTarget.sphereViewer,
  clickType,
  trackAsyncEvent,
  createdAt,
  accessLevel,
}: TrackSnapshotProps): Promise<void> {
  const eventProps: Record<string, string | number> = {
    openFrom,
    creationAge: DateTimeUtils.getAge(createdAt),
  };

  if (numberOfMembers) {
    eventProps.numberOfMembers = numberOfMembers;
  }

  if (openTarget === ProjectLaunchTarget.sphereViewer) {
    eventProps.openTarget = ProjectLaunchTarget.sphereViewer;
  } else {
    eventProps.openTarget = ProjectLaunchTarget.webEditor;
  }

  if (clickType) {
    eventProps.clickType = clickType;
  }

  if (accessLevel) {
    eventProps.accessLevel = accessLevel;
  }

  await trackAsyncEvent({
    name: SnapshotEvents.openSnapshot,
    props: eventProps,
  });
}

/**
 * Sort snapshot members, by name for team first, then by name for members and by email if name isn't available
 * @param members - List of snapshot members
 * @returns Sorted members
 */
export function sortSnapshotMembers(
  members: FaroCoreApiTypes.ProjectMemberBase[]
): FaroCoreApiTypes.ProjectMemberBase[] {
  return sortMembersByNameOrEmail(members);
}
